import { instance as getApi } from '@/helpers/api';
import { getI18nInstance } from '@/i18n';
import { userInfo } from '@/helpers/globals';
import { ROLES } from '@/models/User';
import { IUser } from '@/@types/IUser';

/** only used when localStorage is not available */
let noStorageCsrfToken: string | null = null;

/**
 * Stores the CSRF token in the browser's localStorage
 * for further use during the session.
 *
 * This is needed to support multiple browser windows.
 */
export function storeCsrfToken(token: string) {
    if (Storage) {
        localStorage.setItem('minasa.csrfToken', token);
    } else {
        noStorageCsrfToken = token;
    }
}

/**
 * Retrieves the csrf token from the browser's
 * local storage
 */
export function getCsrfToken(): string {
    if (Storage) {
        return localStorage.getItem('minasa.csrfToken') || '';
    } else {
        return noStorageCsrfToken || '';
    }
}

/**
 * Answer of the first login stage (username / password).
 *
 * Exactly one of these is set:
 * - user:                the login is complete (only for users on the totp exception list)
 * - password_change:     a forced password change comes first
 * - totp_setup_required: the user has no second factor yet -> onboarding
 * - totp_required:       the user must enter a code
 */
export interface LoginResult {
    user?: IUser;
    redirectUrl?: string;
    password_change?: boolean;
    totp_setup_required?: boolean;
    totp_required?: boolean;
}

/** Answer of /LoginLogout/totpSetup */
export interface TotpSetupResult {
    qr_code?: string;
    secret?: string;
    otpauth_uri?: string;
    /** the pending login expired: back to the username / password form */
    login_restart?: boolean;
}

/** Answer of /LoginLogout/totpVerify */
export interface TotpVerifyResult {
    user?: IUser;
    redirectUrl?: string;
    totp_invalid_code?: boolean;
    tries_left?: number;
    /** expired, or no tries left: back to the username / password form */
    login_restart?: boolean;
}

/**
 * The url the user wanted to reach before he was sent to the login.
 */
function getRedirectUrl(): string {
    const split = window.location.href.split('?redirect=');
    return split.length === 2 ? split[1] : '/';
}

export async function login(username: string, password: string): Promise<LoginResult> {
    const vueI18n = getI18nInstance();
    try {
        const response = await getApi().post('/LoginLogout/login', {
            username: username,
            password: password,
        });
        if (response.data.user) {
            return {
                user: response.data.user,
                redirectUrl: getRedirectUrl(),
                password_change: response.data.change_password,
            };
        } else if (response.data.change_password) {
            return {
                password_change: response.data.change_password,
            };
        } else if (response.data.totp_setup_required) {
            return { totp_setup_required: true };
        } else if (response.data.totp_required) {
            return { totp_required: true };
        } else {
            throw new Error(vueI18n.t('denied_wrong_login') as string);
        }
    } catch (e) {
        // if (e.response && e.response.data && e.response.data.error && e.response.data.error.msg) {
        //     throw new Error(vueI18n.t(e.response.data.error.msg));
        // }
        throw new Error(vueI18n.t('denied_wrong_login') as string);
    }
}

/**
 * Second login stage, onboarding: fetches a new TOTP secret as a QR code and as text.
 * Needs the pending login that login() opened.
 */
export async function totpSetup(): Promise<TotpSetupResult> {
    const response = await getApi().post('/LoginLogout/totpSetup', {});
    return response.data || { login_restart: true };
}

/**
 * Second login stage: sends the code from the authenticator app. On success the session
 * is authenticated and the user object comes back.
 */
export async function totpVerify(code: string): Promise<TotpVerifyResult> {
    const response = await getApi().post('/LoginLogout/totpVerify', { code: code });
    const data = response.data || {};
    if (data.user) {
        return { user: data.user, redirectUrl: getRedirectUrl() };
    }
    return data;
}

/** Answer of /LoginLogout/checkResetToken */
export interface CheckResetTokenResult {
    valid: boolean;
    username?: string;
}

/** Answer of /LoginLogout/setNewPassword */
export interface SetNewPasswordResult {
    changed?: boolean;
    invalid_token?: boolean;
    password_too_short?: boolean;
    min_length?: number;
}

/**
 * Checks a "Passwort vergessen" token before the mask is shown, so an expired or already
 * used link does not let the user type a password for nothing.
 */
export async function checkResetToken(token: string): Promise<CheckResetTokenResult> {
    const response = await getApi().post('/LoginLogout/checkResetToken', { token: token });
    return response.data || { valid: false };
}

/**
 * Redeems the token: sets the new password, clears the TOTP setup and activates the account.
 */
export async function setNewPassword(token: string, password: string): Promise<SetNewPasswordResult> {
    const response = await getApi().post('/LoginLogout/setNewPassword', { token: token, password: password });
    return response.data || {};
}

export function userIs(role: string | Array<string>): boolean {
    const user = userInfo();
    if ('string' === typeof role) {
        role = [role];
    }
    if (role instanceof Array) {
        for (let j = 0; j < role.length; j++) {
            const r = (role[j] || '').toLowerCase();
            const userRole = (user.role || '').toLowerCase();
            if (r === userRole) {
                return true;
            }
        }
    }

    return false;
}

export function userIsAdmin(): boolean {
    return userIs([ROLES.ADMIN]);
}

export function userIsStaff(): boolean {
    return userIs([ROLES.ADMIN, ROLES.EDITOR, ROLES.WRITER, ROLES.MANAGER]);
}

export function userIsOrganizer(): boolean {
    return userIs(ROLES.EVENT_ORGANIZER);
}

export function userIsSingleOrganizer() {
    return userIsOrganizer() && userInfo().organizer_group_id <= 0;
}

export async function logout(): Promise<void> {
    await getApi().get('/LoginLogout/logout');
}

export default {
    login,
    logout,
    totpSetup,
    totpVerify,
    checkResetToken,
    setNewPassword,
    // resetPassword,
    // verifyUser
};
